<template>
    <Teleport to="body" v-if="openDrop">
        <div class="fd-drop fd-form" :style="pos" v-bind="$attrs"
             @mouseenter="dropMouseEnter" @mouseleave="dropMouseLeave" @focusin="receivedFocus" @focusout="lostFocus">
            <slot></slot>
        </div>
    </Teleport>
</template>

<script setup lang="ts">

import {computed, type CSSProperties, onMounted, onUnmounted, ref, type Ref, watch} from "vue";
import {injectEnv} from "../Environment";
import {getElementMeasure} from "../helpers/DomUtils";

const props = defineProps({
    tabindex: {
        type: Number,
        default: 10
    },
    /**
     * Minimum required space to drop the panel bellow the editor.
     * If there is less space then required minimum. The component tries to position the panel
     * above the editor.
     * Default: 120 px
     */
    minspace: {
        type: Number,
        default: 120
    }
});

/* Style declaration positioning the drop panel */
const pos = ref<CSSProperties>({});

const env = injectEnv();
const emit = defineEmits(["dropopen", "dropclose"]);

export interface OpenFlags {
    [key: string]: { clear: number, flag: Ref<boolean> }
}

const openFlags: OpenFlags = {
    editorFocus: {
        clear: 0,
        flag: ref<boolean>(false)
    },
    editorMouse: {
        clear: 0,
        flag: ref<boolean>(false)
    },
    panelFocus: {
        clear: 0,
        flag: ref<boolean>(false)
    },
    panelMouse: {
        clear: 0,
        flag: ref<boolean>(false)
    }
}

const openDrop = computed<boolean>(() => {
    return openFlags.editorFocus.flag.value || openFlags.editorMouse.flag.value
        || openFlags.panelFocus.flag.value || openFlags.panelMouse.flag.value;
});

const timeouts: number[] = [];

function killTimeout(flagName: string): void {
    if (openFlags[flagName].clear) {
        clearTimeout(openFlags[flagName].clear);
        openFlags[flagName].clear = 0;
    }
}

function clearFlag(flagName: string, timeout: number = 100) {
    killTimeout(flagName);
    openFlags[flagName].clear = setTimeout(() => {
        openFlags[flagName].flag.value = false;
        killTimeout(flagName);
    }, timeout);
}

function setFlag(flagName: string) {
    killTimeout(flagName);
    openFlags[flagName].flag.value = true;
}

const ELM_SPACING = 1;
const EDGE_SPACING = 2;

function repositionDropDown(positionElement: Ref<HTMLElement | null>) {
    if (positionElement.value != null) {
        const m = getElementMeasure(positionElement.value);
        // This is version for position:fixed
        if (m.spaceBottom < props.minspace && m.spaceTop > m.spaceBottom) {
            // place above
            pos.value = {
                bottom: (m.viewportHeight - m.viewTop + ELM_SPACING) + "px",
                left: m.viewLeft + "px",
                top: "auto",
                maxHeight: (m.spaceTop - ELM_SPACING - EDGE_SPACING) + "px"
            }
        } else {
            // place bellow
            pos.value = {
                top: (m.viewTop + m.height + ELM_SPACING) + "px",
                left: m.viewLeft + "px",
                bottom: "auto",
                maxHeight: (m.spaceBottom - ELM_SPACING - EDGE_SPACING) + "px"
            }
        }
        // This is version for position:absolute (buggy)
        // if (m.spaceBottom < props.minspace && m.spaceTop > m.spaceBottom) {
        //     // place above
        //     pos.value = {
        //         bottom: (-1 * m.scrollTop + (m.viewportHeight - m.viewTop)) + "px",
        //         left: m.left + "px",
        //         top: "auto",
        //         maxHeight: m.spaceTop + "px"
        //     }
        // } else {
        //     // place bellow
        //     pos.value = {
        //         top: (m.top + m.height) + "px",
        //         left: m.left + "px",
        //         bottom: "auto",
        //         maxHeight: m.spaceBottom + "px"
        //     }
        // }
        //console.log("DropDown\nmeasure: " + JSON.stringify(m) + "\nposition: " + JSON.stringify(pos.value));
    }
}

let lastPositionElement:Ref<HTMLElement | null> = ref(null);
function editorFocus(positionElement: Ref<HTMLElement | null>): void {
    lastPositionElement = positionElement;
    repositionDropDown(positionElement);
    setFlag("editorFocus");
}

function editorBlur() {
    clearFlag("editorFocus");
}

function editorMouseEnter() {
    setFlag("editorMouse");
}

function editorMouseLeave() {
    clearFlag("editorMouse");
}

function receivedFocus() {
    setFlag("panelFocus");
}

function lostFocus() {
    clearFlag("panelFocus");
}

function dropMouseEnter() {
    setFlag("panelMouse");
}

function dropMouseLeave() {
    clearFlag("panelMouse");
}

defineExpose({editorFocus, editorBlur, editorMouseEnter, editorMouseLeave, repositionDropDown})

function scrollHook() {
    repositionDropDown(lastPositionElement);
}

onMounted(() => {
    document.addEventListener("scroll", scrollHook);
});

onUnmounted(() => {
    document.removeEventListener("scroll", scrollHook);
});

watch(() => openDrop.value, (state:boolean)=> {
	emit(state ? "dropopen" : "dropclose");
})
</script>

<style>
.fd-drop {
    /* position: absolute; */
    position: fixed;
    box-sizing: border-box;
    background-color: var(--bg);
    color: var(--fg);
    border: 1px solid var(--border-color);
	border-radius: var(--input-radius);
    box-shadow: var(--panel-shadow);
    overflow-y: auto;
	z-index: var(--drop-z-index);
}

.fd-drop::-webkit-scrollbar {
    display: none;
}

.fd-drop:focus {
    border: 1px solid var(--outline-color);
}
</style>
